import React, { useState } from "react";
import { useUser } from "@clerk/clerk-react";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { createCheckout } from "@/lib/api";
import { useAcceptanceGate } from "@/components/legal/useAcceptanceGate";
import EmailGate from "@/components/EmailGate";
import EnquiryForm from "@/components/EnquiryForm";
import { LAUNCH_PRICING_ACTIVE, AUDIT_STANDARD_PRICE, AUDIT_LAUNCH_PRICE } from "@/lib/brand";

const TIERS = [
  {
    id: "teaser", tag: "Tier 01", name: "Margin Risk Sweep", price: "Free", priceNote: "5 per account, sign in to run one",
    // The three `line` values carry one narrative: outside-in, then inside-out,
    // then month to month. They are written to be read in sequence down the
    // page, so changing one without the others breaks the story.
    line: "What can we see from the outside?",
    features: ["We name the products - the packs, prices and bands costing you money, by name", "Your prices and your competitor's on one shared ladder, band by band", "Unit and pack economics - price per ml, gram or item, read from your own titles", "Multipacks priced above your own singles, with the worst one quoted", "Every finding says what public data cannot settle, and which audit exhibit does"],
    cta: "Run a sweep", highlight: false, packageId: null, contact: false,
  },
  {
    // The standard price is A$2,500 and A$990 is a launch rate for founding
    // clients. Presented as "Standard price" struck through, NOT as a "was"
    // price: a was/now claim requires the higher price to have been the actual
    // selling price for a reasonable period beforehand, and nothing has been
    // sold yet. What makes this presentation truthful is the intention to
    // actually charge A$2,500 when the launch period ends - see the note in
    // docs/GO-LIVE.md.
    id: "margin_audit", tag: "Tier 02", name: "Margin Audit",
    line: "What is actually happening inside the business?",
    // Driven by the single LAUNCH_PRICING_ACTIVE switch in brand.js, not two
    // hardcoded strings. While the launch is active the buyer is charged the
    // launch rate with the standard price struck through; when the operator
    // ends the launch (one flag), the standard price shows plainly with no
    // strikethrough and no launch note - so the strikethrough can never outlive
    // the genuine intention to charge A$2,500 that makes it truthful.
    price: LAUNCH_PRICING_ACTIVE ? AUDIT_LAUNCH_PRICE : AUDIT_STANDARD_PRICE,
    priceNote: "one-time",
    standardPrice: LAUNCH_PRICING_ACTIVE ? AUDIT_STANDARD_PRICE : null,
    launchNote: LAUNCH_PRICING_ACTIVE ? "Launch offer · founding clients" : null,
    features: ["Opens by answering the question you ask us at intake, in your words", "The contribution-margin waterfall against your actual P&L, and the cost line actually binding it", "One order walked down that same P&L, so the decision sits in the unit you think in", "Your acquisition break-even - the return paid has to clear, from your margin, not a category average", "Every lever with the unit-volume fall it can absorb before it stops paying", "The margin bridge: what we can prove, what is held back for evidence, and the test that releases it", "Every competitor you name, read the same day, and whether each is a fair comparison at all", "Written action plan (the analyst's read), walked through on a call"],
    cta: "Commission Audit", highlight: true, packageId: "margin_audit", contact: false,
    // Sold only to Australian businesses - see backend/market.py and the
    // `requires_supported_market` flag on PACKAGES. Drives the pre-checkout
    // question and the scope line below the price.
    marketBound: true,
    scopeNote: "Built for Australian online-only brands - we ask where you trade before checkout",
  },
  {
    // Name matches Schedule 1B of the services agreement deliberately - a buyer
    // signing "Monthly Retainer" should see "Monthly Retainer". The
    // repositioning happens in `line` and the features, not by renaming the
    // thing the contract describes.
    id: "retainer", tag: "Tier 03", name: "Monthly Retainer", price: "A$4,500", priceNote: "per month \u00b7 invoiced monthly",
    line: "Your commercial intelligence function, on a monthly cycle.",
    creditNote: "Your Margin Audit fee comes off the first month, if you start within 30 days",
    // The four-week cadence, so the fee buys something legible rather than a
    // feature list. Weeks are the rhythm of the work, not a delivery promise -
    // the contractual commitments live in Schedule 1B.
    features: ["Week 1 - what the market did: competitor pricing, promotions, markdowns, range changes", "Week 2 - what your economics did: contribution margin, discounting, freight, acquisition", "Week 3 - the decision pack: 3-5 decisions, each sized, with the effort each takes", "Week 4 - the call: agree what changes, and what to watch next month", "Direct analyst access between calls", "Invoiced monthly - not a subscription, and no lock-in"],
    cta: "Get in touch", highlight: false, packageId: null, contact: true,
  },
];

export default function PricingMatrix({ reportId }) {
  // A signed-in buyer has already given us this address, and the sweep they
  // just ran is attached to it. Asking again reads as though the account did
  // not register - on the one screen where hesitation costs A$990.
  const { user } = useUser();
  const accountEmail = user?.primaryEmailAddress?.emailAddress || null;
  const [busy, setBusy] = useState(null);
  const [gate, setGate] = useState({ open: false, tier: null });
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const { guard, gateNode } = useAcceptanceGate();

  const openTier = (tier) => {
    if (tier.contact) {
      // Was a mailto: on a domain we do not own, which bounced or silently did
      // nothing depending on the user's mail setup - on the highest-value CTA.
      setEnquiryOpen(true);
      return;
    }
    if (!tier.packageId) {
      document.getElementById("intake")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    // A MARKET-BOUND TIER ALWAYS OPENS THE GATE, even for a signed-in buyer.
    //
    // The email shortcut below exists so a known buyer is not asked twice for
    // an address we hold. It cannot be taken when the sale itself depends on a
    // question we have not asked: skipping the modal for a signed-in buyer
    // would take A$990 from a foreign business without ever putting the scope
    // in front of them, which is the whole failure this gate prevents.
    if (accountEmail && !tier.marketBound) {
      proceed(accountEmail, null, tier);
      return;
    }
    setGate({ open: true, tier });
  };

  const proceed = async (email, country, tierArg) => {
    // The tier is passed when we skip the modal, because state has not been
    // set in that path and reading gate.tier would find null.
    const tier = tierArg || gate.tier;
    if (!tier) return;
    try {
      setBusy(tier.id);
      // The services agreement carries the liability cap. It has to be accepted
      // before Stripe sees the request, never on the way back from it.
      await guard("checkout", async () => {
        const { url } = await createCheckout(tier.packageId, reportId, email, country);
        if (!url) throw new Error("No checkout url");
        window.location.href = url;
      });
      setBusy(null);
      setGate({ open: false, tier: null });
    } catch (err) {
      // 409 is the market boundary, not a transient failure, and telling the
      // buyer to "try again" would be untrue - retrying cannot succeed.
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.error === "outside_supported_market") {
        toast.error(detail.message || "This product is sold in Australia only.");
        setBusy(null);
        return;
      }
      toast.error("Unable to start checkout. Please try again.");
      setBusy(null);
      setGate({ open: false, tier: null });
    }
  };

  return (
    <section id="pricing" className="border-t border-[#c9c3b8] bg-[#e8e4df]">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 md:py-20">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="tick mb-4">05 · Commercial ladder</div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[#171717] leading-[1.02]">
            Three tiers. The first one is free.
          </h2>
          <p className="mt-5 font-mono-tech text-[13px] text-[#404040]">
            The sweep reads what a store publishes. The audit reads the business. The free tier
            is real work, not a demo — it runs the same measured feeds the paid audit builds on.
            Above it: your own P&amp;L reconciled against those numbers, then the commercial
            decisions owned month to month.
          </p>
          {/* W3 + W6. What we refuse to say is the differentiator, and it lived
              on page fifteen of a paid PDF. Every buyer weighing A$990 against a
              subscription dashboard should be able to read the standard first. */}
          <p className="mt-4 font-mono-tech text-[12px] text-[#525252]">
            {/* The "How we refuse to guess" link is removed while /method is held
                back for review. The sentence stands on its own and still makes the
                claim; only the destination is gone. See App.js. */}
            Every figure carries what stands behind it, and where your data cannot support a
            recommendation we withhold it and name the test that would settle it.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 stagger-children" data-testid="pricing-grid">
          {TIERS.map((t) => (
            <article
              key={t.id}
              data-testid={`tier-${t.id}`}
              className={`flex flex-col ${
                t.highlight
                  ? "bg-[#171717] text-[#f5f2ed] border border-[#171717] rounded-[8px] shadow-[0_18px_44px_-12px_rgba(10,10,10,0.45)]"
                  : "card-cream card-lift"
              }`}
            >
              <header className={`p-6 border-b flex items-baseline justify-between ${t.highlight ? "border-[#404040]" : "border-[#ddd8ce]"}`}>
                <span className={`tick ${t.highlight ? "text-[#e88a51]" : "text-[#9a3412]"}`}>{t.tag}</span>
                {t.highlight && <span className="tick">Recommended</span>}
              </header>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className={`font-serif-display text-2xl mb-1 ${t.highlight ? "text-[#f5f2ed]" : "text-[#171717]"}`}>{t.name}</h3>
                {t.line && (
                  <p
                    data-testid={`tier-line-${t.id}`}
                    className={`font-mono-tech text-[11.5px] leading-relaxed mb-4 ${
                      t.highlight ? "text-[#f5f2ed]/70" : "text-[#525252]"
                    }`}
                  >
                    {t.line}
                  </p>
                )}
                <div className="mb-6">
                  {t.standardPrice && (
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className={`tick ${t.highlight ? "text-[#f5f2ed]/60" : "text-[#525252]"}`}>Standard price</span>
                      <span
                        data-testid="standard-price"
                        className={`font-serif-display text-xl line-through decoration-[1.5px] ${
                          t.highlight ? "text-[#f5f2ed]/55 decoration-[#f5f2ed]/55" : "text-[#737373] decoration-[#9a3412]"
                        }`}
                      >
                        {t.standardPrice}
                      </span>
                    </div>
                  )}
                  <div className="flex items-baseline gap-2">
                    <span className={`font-serif-display text-4xl ${t.highlight ? "text-[#f5f2ed]" : "text-[#171717]"}`}>{t.price}</span>
                    <span className={`tick ${t.highlight ? "text-[#f5f2ed]/60" : ""}`}>{t.priceNote}</span>
                  </div>
                  {t.creditNote && (
                    <p
                      data-testid="credit-note"
                      className="mt-2 font-mono-tech text-[11px] leading-relaxed text-[#9a3412]"
                    >
                      {t.creditNote}
                    </p>
                  )}
                  {t.scopeNote && (
                    <p
                      data-testid={`scope-note-${t.id}`}
                      className={`mt-2 font-mono-tech text-[11px] tracking-wide ${
                        t.highlight ? "text-[#f5f2ed]/60" : "text-[#525252]"
                      }`}
                    >
                      {t.scopeNote}
                    </p>
                  )}
                  {t.launchNote && (
                    <p
                      data-testid="launch-note"
                      className={`mt-2 font-mono-tech text-[11px] tracking-wide ${
                        t.highlight ? "text-[#e88a51]" : "text-[#9a3412]"
                      }`}
                    >
                      {t.launchNote}
                    </p>
                  )}
                </div>
                <ul className="space-y-3 mb-8 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className={`flex items-start gap-3 font-mono-tech text-[12px] ${t.highlight ? "text-[#f5f2ed]/85" : "text-[#404040]"}`}>
                      <Check size={14} className="mt-[3px] text-[#9a3412] shrink-0" /><span>{f}</span>
                    </li>
                  ))}
                </ul>
                {t.id === "margin_audit" && (
                  <p className={`text-[11px] font-mono-tech mb-4 -mt-4 leading-relaxed ${t.highlight ? "text-[#f5f2ed]/50" : "text-[#525252]"}`}>
                    Built entirely from the figures you provide — no bank statements or platform logins requested, by design.
                  </p>
                )}
                <button
                  className={t.highlight ? "btn-amber w-full justify-center" : "btn-primary w-full justify-center"}
                  onClick={() => openTier(t)}
                  disabled={busy === t.id}
                  data-testid={`tier-cta-${t.id}`}
                >
                  {busy === t.id ? "Opening Stripe…" : (<>{t.cta} <ArrowRight size={14} /></>)}
                </button>
              </div>
            </article>
          ))}
        </div>
      </div>
      {gateNode}
      <EnquiryForm open={enquiryOpen} onOpenChange={setEnquiryOpen} />
      <EmailGate
        open={gate.open}
        onOpenChange={(v) => setGate((g) => ({ ...g, open: v }))}
        tier={gate.tier}
        onSubmit={proceed}
        loading={!!busy}
        askCountry={!!gate.tier?.marketBound}
        initialEmail={accountEmail || ""}
      />
    </section>
  );
}
